import React, { useRef, useState } from "react";
import { FiFileText, FiGrid } from "react-icons/fi";
import { MoveToFolderMenu } from "./MoveToFolderMenu";
import { MARK_STYLES, formatTime, highlightKeyword, truncateText } from "../historyFormat";
import { useOutsidePress } from "../useHistory";

const MAX_TITLE_LEN = 100;

// One conversation in the list: its name (renamed in place), the recap or a
// preview, the time / messages / folder chips, and the row's actions. The
// expanded transcript is passed in as children.
export function ConversationRow({
  conv,
  language,
  searchKeyword,
  showFolderChip,
  isExpanded,
  onToggle,
  isRenaming,
  onStartRename,
  onCancelRename,
  onRename,
  isExporting,
  onExport,
  onDelete,
  folderMenu,
  children,
}) {
  // The rename draft. Seeded with the existing custom title only: pre-filling
  // the auto preview would make "keep the current name" indistinguishable
  // from "adopt the preview as a custom title".
  const [draft, setDraft] = useState("");

  return (
    <article
      className={`group rounded-xl border bg-white dark:bg-gray-800 shadow-sm transition-[box-shadow,border-color] ${
        isExpanded
          ? "border-orange-300 dark:border-orange-500/50 shadow-md"
          : "border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600"
      }`}
    >
      {/* Row header. Only the chevron and the body below the title toggle the
          row — the title line sits outside that button so renaming can happen
          in place, with the rest of the row still visible. */}
      <div className="flex items-start gap-2 p-3 sm:p-4">
        <button
          className="flex-shrink-0 mt-0.5"
          aria-expanded={isExpanded}
          aria-label={language === "zh" ? "展開對話" : "Expand conversation"}
          onClick={onToggle}
        >
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
              isExpanded ? "rotate-90 text-orange-500" : "group-hover:text-gray-500"
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div className="flex-1 min-w-0">
          {isRenaming ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                className="flex-1 min-w-0 px-2 py-1 rounded-md border border-orange-400 dark:border-orange-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm font-medium outline-none focus:ring-2 focus:ring-orange-500"
                maxLength={MAX_TITLE_LEN}
                value={draft}
                placeholder={
                  language === "zh"
                    ? "輸入對話標題（留空恢復預設）"
                    : "Name this conversation (empty to reset)"
                }
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onRename(draft);
                  if (e.key === "Escape") onCancelRename();
                }}
              />
              <button
                className="shrink-0 px-2.5 py-1 rounded-md bg-orange-500 text-white text-xs font-medium hover:bg-orange-600 transition-colors"
                onClick={() => onRename(draft)}
              >
                {language === "zh" ? "儲存" : "Save"}
              </button>
              <button
                className="shrink-0 px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                onClick={onCancelRename}
              >
                {language === "zh" ? "取消" : "Cancel"}
              </button>
            </div>
          ) : (
            <div className="flex items-start gap-1.5">
              {/* Three sources, in order of how deliberate they are: a name
                  the user chose, then the LLM's, then the raw first question.
                  A name someone typed is never overridden by a generated one. */}
              {conv.title || conv.summary ? (
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words min-w-0">
                  {conv.title || conv.summary.title}
                </p>
              ) : (
                <p
                  className={`text-sm font-medium text-gray-900 dark:text-gray-100 break-words min-w-0 ${MARK_STYLES}`}
                  dangerouslySetInnerHTML={{
                    __html: highlightKeyword(
                      truncateText(
                        conv.user_preview ||
                          (language === "zh" ? "(無訊息)" : "(No message)"),
                      ),
                      searchKeyword,
                    ),
                  }}
                />
              )}
              {/* Beside the name it edits, rather than in the action cluster
                  on the right. */}
              <button
                className="flex-shrink-0 mt-0.5 text-gray-300 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-300 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-all"
                title={language === "zh" ? "重新命名" : "Rename"}
                onClick={() => {
                  setDraft(conv.title || "");
                  onStartRename();
                }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Everything below the title still toggles the row, so the click
              target stays most of the card even though the title is now its
              own line. */}
          <div
            role="button"
            tabIndex={0}
            className="cursor-pointer"
            onClick={onToggle}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggle();
              }
            }}
          >
            {/* The recap replaces the agent preview, which is a reply cut off
                mid-sentence. */}
            {conv.summary ? (
              // Clamped while collapsed so every row is the same height; the
              // whole recap once the row is open, which is the only copy of it
              // there is.
              <p
                className={`text-sm text-gray-500 dark:text-gray-400 break-words mt-1 ${
                  isExpanded ? "" : "line-clamp-2"
                }`}
              >
                {conv.summary.text}
              </p>
            ) : (
              <p
                className={`text-sm text-gray-500 dark:text-gray-400 break-words mt-1 ${MARK_STYLES}`}
                dangerouslySetInnerHTML={{
                  __html: highlightKeyword(
                    truncateText(
                      conv.agent_preview ||
                        (language === "zh" ? "(無回應)" : "(No response)"),
                      90,
                    ),
                    searchKeyword,
                  ),
                }}
              />
            )}
            <div className="flex items-center flex-wrap gap-1.5 mt-2.5 text-xs text-gray-400 dark:text-gray-500">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700/70">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {formatTime(conv.created_at)}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700/70">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.9 9.9 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                {conv.message_count} {language === "zh" ? "則訊息" : "messages"}
              </span>
              {/* Hidden inside a folder, where every row would carry the same
                  chip. */}
              {conv.folder && showFolderChip && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                    />
                  </svg>
                  {conv.folder}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Export does not require the row to be open: the transcript it
            needs is fetched on click when it has not been loaded already. */}
        <ExportMenu language={language} busy={isExporting} onExport={onExport} />

        <MoveToFolderMenu conv={conv} language={language} {...folderMenu} />

        <button
          className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-300 dark:text-red-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 dark:hover:text-red-400 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-all"
          title={language === "zh" ? "刪除" : "Delete"}
          onClick={onDelete}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {children}
    </article>
  );
}

// The download button and the two formats it offers: a PDF to read, and a CSV
// for Excel with one row per message.
function ExportMenu({ language, busy, onExport }) {
  const zh = language === "zh";
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  useOutsidePress(rootRef, open, () => setOpen(false));

  const formats = [
    {
      format: "pdf",
      Icon: FiFileText,
      label: zh ? "PDF 檔" : "PDF",
      hint: zh ? "適合閱讀與列印" : "For reading and printing",
    },
    {
      format: "csv",
      Icon: FiGrid,
      label: zh ? "CSV 檔（Excel）" : "CSV (Excel)",
      hint: zh ? "每則訊息一列，可排序與篩選" : "One row per message, to sort and filter",
    },
  ];

  return (
    <div
      ref={rootRef}
      className="relative flex-shrink-0"
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        title={zh ? "匯出" : "Export"}
        aria-label={zh ? "匯出" : "Export"}
        disabled={busy}
        onClick={() => setOpen((cur) => !cur)}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-all disabled:opacity-40 ${
          open
            ? "text-orange-600 bg-orange-50 dark:text-orange-300 dark:bg-orange-500/15"
            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
        }`}
      >
        {busy ? (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.37 0 0 5.37 0 12h4z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        )}
      </button>
      {open && (
        <div
          role="menu"
          aria-label={zh ? "匯出" : "Export"}
          className="absolute right-0 top-full z-30 mt-1 w-60 p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl text-left"
        >
          {formats.map(({ format, Icon, label, hint }) => (
            <button
              key={format}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onExport(format);
              }}
              className="w-full flex items-start gap-2.5 px-2 py-2 rounded-lg text-left outline-none hover:bg-orange-50 focus:bg-orange-50 dark:hover:bg-orange-500/15 dark:focus:bg-orange-500/15"
            >
              <Icon aria-hidden="true" className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-800 dark:text-gray-100">{label}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
